import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/homeData';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideDropdownRef.current && !guideDropdownRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const textColor = scrolled ? 'text-foreground-950' : 'text-background-50';
  const textMuted = scrolled ? 'text-foreground-700' : 'text-background-50/80';
  const textHover = scrolled ? 'hover:text-foreground-950' : 'hover:text-background-50';
  const logoColor = scrolled ? 'text-foreground-950' : 'text-background-50';
  const dropdownBg = scrolled ? 'bg-background-50' : 'bg-background-50';
  const dropdownBorder = 'border-background-200/70';
  const navBg = scrolled
    ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
    : 'bg-transparent';

  const guideArticles = [
    { slug: 'how-to-use-vigorbuy-spreadsheet', label: 'Beginner Guide' },
    { slug: 'vigorbuy-shipping-protection-guide', label: 'Shipping Guide' },
    { slug: 'is-vigorbuy-safe-review', label: 'Safety Review' },
    { slug: 'vigorbuy-seller-verification-tips', label: 'Seller Verification' },
    { slug: 'vigorbuy-vs-pandabuy-comparison', label: 'vs Pandabuy' },
    { slug: 'best-vigorbuy-spreadsheet-products', label: 'Best Products' },
    { slug: 'best-shoes-vigorbuy-spreadsheet', label: 'Best Shoes' },
    { slug: 'best-clothing-vigorbuy-deals', label: 'Best Clothing' },
    { slug: 'vigorbuy-budget-products-under-50', label: 'Budget Buys' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className={`text-xl md:text-2xl font-heading font-bold ${logoColor} tracking-tight whitespace-nowrap`}
          >
            VigorBuy
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium ${textMuted} ${textHover} transition-colors whitespace-nowrap`}
            >
              Home
            </Link>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => {
                  setDropdownOpen(!dropdownOpen);
                  setGuideDropdownOpen(false);
                }}
                className={`flex items-center gap-1 text-sm font-medium ${textMuted} ${textHover} transition-colors whitespace-nowrap cursor-pointer`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-60 ${dropdownBg} rounded-lg border ${dropdownBorder} py-2 shadow-lg`}>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-foreground-400`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Shopping Guide Dropdown */}
            <div ref={guideDropdownRef} className="relative">
              <button
                onClick={() => {
                  setGuideDropdownOpen(!guideDropdownOpen);
                  setDropdownOpen(false);
                }}
                className={`flex items-center gap-1 text-sm font-medium ${textMuted} ${textHover} transition-colors whitespace-nowrap cursor-pointer`}
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${guideDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {guideDropdownOpen && (
                <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-52 ${dropdownBg} rounded-lg border ${dropdownBorder} py-2 shadow-lg`}>
                  {guideArticles.map((g) => (
                    <Link
                      key={g.slug}
                      to={`/article/${g.slug}`}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                      onClick={() => setGuideDropdownOpen(false)}
                    >
                      {g.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href="#safety"
              className={`text-sm font-medium ${textMuted} ${textHover} transition-colors whitespace-nowrap`}
            >
              Safety
            </a>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2 text-sm font-medium border rounded-md transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-primary-700 border-primary-300 hover:bg-primary-50'
                  : 'text-background-50 border-background-50/30 hover:bg-background-50/10'
              }`}
            >
              VigorBuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold text-background-50 bg-primary-500 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${textColor}`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-6 py-4 max-h-[70vh] overflow-y-auto">
          <div className="flex flex-col gap-2">
            <Link
              to="/"
              className="flex items-center gap-3 text-sm font-medium text-foreground-700 py-2.5"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-4-line text-foreground-400"></i>
              </span>
              Home
            </Link>

            {/* Mobile Categories */}
            <div>
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full text-sm font-semibold text-foreground-500 py-2.5 cursor-pointer"
              >
                <span>Categories</span>
                <i className={`${mobileCatOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base transition-transform`}></i>
              </button>
              {mobileCatOpen && (
                <div className="ml-2 grid grid-cols-2 gap-1 mt-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 text-xs text-foreground-600 py-2 px-2 rounded-md hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-foreground-400 text-xs`}></i>
                      </span>
                      <span className="truncate">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Shopping Guide */}
            <div>
              <button
                onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
                className="flex items-center justify-between w-full text-sm font-semibold text-foreground-500 py-2.5 cursor-pointer"
              >
                <span>Shopping Guide</span>
                <i className={`${mobileGuideOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base transition-transform`}></i>
              </button>
              {mobileGuideOpen && (
                <div className="ml-2 flex flex-col mt-1">
                  {guideArticles.map((g) => (
                    <Link
                      key={g.slug}
                      to={`/article/${g.slug}`}
                      className="flex items-center gap-2 text-xs text-foreground-600 py-2 px-2 rounded-md hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-article-line text-foreground-400 text-xs"></i>
                      </span>
                      {g.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href="#safety"
              className="flex items-center gap-3 text-sm font-medium text-foreground-700 py-2.5"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shield-check-line text-foreground-400"></i>
              </span>
              Safety
            </a>

            <div className="flex flex-col gap-2 mt-2 pt-3 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-2.5 text-sm font-medium text-primary-700 border border-primary-300 rounded-md text-center hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                VigorBuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 rounded-md text-center hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}